<script setup lang="ts">
// NPM
import { computed } from 'vue';

// Components
import { BaseBadge, BaseButton } from '@magma-app/magma-lapilli';

// Types
import type { PropType } from 'vue';

// Helpers
import AnalyticsHelper from '@/helpers/analytics';

// Stores
import { useCommonStore } from '@/stores/common';
import { useRelationsStore } from '@/stores/relations';
const commonStore = useCommonStore();
const relationsStore = useRelationsStore();

// Props
const props = defineProps({
  relationMomentId: {
    type: Number,
    required: true,
  },
  statusRelationMoment: {
    type: String as PropType<
      'ongoing' | 'waitingAdminValidation' | 'validated'
    >,
    required: true,
  },
  statusRelation: {
    type: String as PropType<'accepted' | 'rejected' | null>,
    required: true,
  },
  descriptionCampaign: {
    type: String,
    default: '',
  },
  instructionFeedback: {
    type: String,
    default: '',
  },
  needFeedbackRating: {
    type: Boolean,
    default: false,
  },
  needFeedbackComment: {
    type: Boolean,
    default: false,
  },
  needFeedbackFile: {
    type: Boolean,
    default: false,
  },
  disableValidation: {
    type: Boolean,
    default: false,
  },
  persona: {
    type: String as PropType<'helper' | 'prospect'>,
    default: '',
  },
});

// Computed properties
const currentRelationMomentStatus = computed(() => {
  // Get the latest status from the store
  const storeRelation = relationsStore.relation;
  if (
    storeRelation?.relation?.relationMoment?.relationMomentId ===
    props.relationMomentId
  ) {
    return storeRelation.relation?.relationMoment?.status;
  }
  return props.statusRelationMoment;
});

const canMarkMomentAsDone = computed(() => {
  return currentRelationMomentStatus.value === 'ongoing' && !props.disableValidation;
});

const showActionFooter = computed(() => {
  if (props.persona === 'helper') {
    return (
      canMarkMomentAsDone.value
      || currentRelationMomentStatus.value === 'waitingAdminValidation'
      || currentRelationMomentStatus.value === 'validated'
    );
  }

  return canMarkMomentAsDone.value || currentRelationMomentStatus.value === 'validated';
});

// Event listeners
const onMarkMomentAsDone = () => {
  commonStore.setFeedbackRelationModal({
    open: true,
    persona: props.persona,
    needFeedbackRating: props.needFeedbackRating,
    needFeedbackComment: props.needFeedbackComment,
    needFeedbackFile: props.needFeedbackFile,
    relationMomentId: props.relationMomentId,
    instructionFeedback: props.instructionFeedback,
  });

  AnalyticsHelper.trackEvent('validate_moment', {
    event_properties: 'timeline',
    user_properties: 'helper',
  });
};
</script>

<!-- 	AMBASSADOR SIDE 	-->
<template>
  <div v-if="props.persona === 'helper'">
    <div
      class="text-mgm-txt-sm font-normal text-foreground-default pb-[64px] md:pb-0"
    >
      <p v-html="props.descriptionCampaign"></p>
    </div>
    <div
      v-if="showActionFooter"
      class="step-4 gradient p-4 pb-6 pt-8 fixed sm:static bottom-0 left-0 w-full sm:px-0"
    >
      <BaseButton
        v-if="canMarkMomentAsDone"
        size="md"
        class="w-full md:w-auto"
        state="prmr"
        @click="onMarkMomentAsDone"
      >
        {{ $t('generic.validateExchange') }}
      </BaseButton>

      <!-- IF WAITING ADMIN VALIDATION -->
      <BaseBadge
        v-if="
          currentRelationMomentStatus === 'waitingAdminValidation' ||
          (currentRelationMomentStatus === 'validated' && !statusRelation)
        "
        size="lg"
        color="yellow"
        :text="$t('baseRelationMoment.status.waitingAdminValidation')"
        class="w-full md:w-fit"
      />

      <!-- IF VALIDATED BY ADMIN -->
      <BaseBadge
        v-if="
          currentRelationMomentStatus === 'validated' &&
          statusRelation === 'accepted'
        "
        size="lg"
        color="green"
        :text="$t('baseRelationMoment.status.validated')"
        class="w-full md:w-fit"
      />

      <!-- IF REJECTED BY ADMIN -->
      <BaseBadge
        v-if="
          currentRelationMomentStatus === 'validated' &&
          statusRelation === 'rejected'
        "
        size="lg"
        color="red"
        :text="$t('baseRelationMoment.status.rejected')"
        class="w-full md:w-fit"
      />
    </div>
  </div>

  <!-- 	PROSPECT SIDE 	-->
  <div v-else>
    <div
      class="text-mgm-txt-sm font-normal text-foreground-default pb-[64px] md:pb-0 flex flex-col gap-4"
    >
      <p>
        {{ $t('baseRelationMoment.generic.first') }}
        {{ relationsStore.relationHelpee?.relation?.helper.firstname }}.
      </p>
      <p>
        {{ $t('baseRelationMoment.generic.second') }}
        {{ relationsStore.relationHelpee?.relation?.helper.firstname }}
        {{ $t('baseRelationMoment.generic.third') }}
      </p>
    </div>
    <div
      v-if="showActionFooter"
      class="step-4 gradient p-4 pb-6 pt-8 fixed sm:static bottom-0 left-0 w-full sm:px-0"
    >
      <BaseButton
        v-if="canMarkMomentAsDone"
        size="md"
        class="w-full md:w-auto"
        state="prmr"
        @click="onMarkMomentAsDone"
      >
        {{ $t('generic.validateExchange') }}
      </BaseButton>

      <!-- IF VALIDATED BY PROSPECT -->
      <BaseBadge
        v-if="currentRelationMomentStatus === 'validated'"
        size="lg"
        color="green"
        :text="$t('baseRelationMoment.status.validated')"
        class="w-full md:w-fit"
      />
    </div>
  </div>
</template>

<style scoped>
.gradient {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, white 37.3%);
}
</style>
